// Round 132b (A): a page that was open during a deploy asks for a lazy route chunk whose hashed file the new release no longer contains. Firebase Hosting then
// answers the missing .js with the SPA fallback (index.html, status 200) and the browser reports "Failed to fetch dynamically imported module: .../LevelConfirmed-xxxx.js".
// A tester saw exactly that at the end of placement, minutes after a release. The cure is standard: reload the page ONCE (index.html is fetched fresh and names the new
// chunks) and keep the same route. A sessionStorage guard makes a reload loop impossible: after one automatic reload, a second failure within GUARD_WINDOW_MS is shown to
// the learner (the existing error card) instead of reloading again.
//
// Imports nothing from apiProxy (no import.meta) so it is unit-testable.

export const CHUNK_RELOAD_KEY = 'emi:chunk-reload-at';
/** a second chunk failure within this window after an automatic reload is NOT reloaded again */
export const GUARD_WINDOW_MS = 30_000;

const PATTERNS = [
  /Failed to fetch dynamically imported module/i,   // Chromium
  /error loading dynamically imported module/i,      // Firefox
  /Importing a module script failed/i,               // Safari
  /Unable to preload CSS/i,                          // Vite
  /Loading chunk [\w-]+ failed/i,                    // webpack-style
  /ChunkLoadError/i,
];

/** True when `err` (an Error, an event payload or a string) is the "this build's chunk is gone" failure. */
export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const text = typeof err === 'string' ? err : `${(err as { name?: string }).name ?? ''} ${(err as { message?: string }).message ?? ''}`;
  return PATTERNS.some((p) => p.test(text));
}

interface Deps { storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null; now?: () => number; reload?: () => void }

function store(deps: Deps): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
  if (deps.storage !== undefined) return deps.storage;
  try { return window.sessionStorage; } catch { return null; }
}

/**
 * Reloads the page once for a stale-chunk failure. Returns true when a reload was started (the caller should render nothing / a spinner), false when the guard
 * says a reload already happened a moment ago (the caller shows its error card). Without sessionStorage (blocked) no automatic reload is attempted at all: it
 * could not be made loop-proof.
 */
export function reloadOnceForStaleChunk(deps: Deps = {}): boolean {
  const s = store(deps);
  if (!s) return false;
  const now = (deps.now ?? Date.now)();
  try {
    const last = Number(s.getItem(CHUNK_RELOAD_KEY));
    if (last && now - last < GUARD_WINDOW_MS) return false;
    s.setItem(CHUNK_RELOAD_KEY, String(now));
  } catch { return false; }
  (deps.reload ?? (() => window.location.reload()))();
  return true;
}

/** Called at startup: forgets an old guard so a LATER deploy in the same tab can reload once again. */
export function clearStaleChunkGuard(deps: Deps = {}): void {
  const s = store(deps);
  if (!s) return;
  const now = (deps.now ?? Date.now)();
  try {
    const last = Number(s.getItem(CHUNK_RELOAD_KEY));
    if (last && now - last >= GUARD_WINDOW_MS) s.removeItem(CHUNK_RELOAD_KEY);
  } catch { /* ignore */ }
}

/** Vite fires `vite:preloadError` when a dynamic import's preload fails; preventDefault() stops Vite re-throwing it. */
export function installPreloadErrorHandler(target: Pick<Window, 'addEventListener'> = window): void {
  clearStaleChunkGuard();
  target.addEventListener('vite:preloadError', (event: Event) => {
    if (reloadOnceForStaleChunk()) event.preventDefault();
  });
}
